import { FC } from 'react';
import { Box, Image, Text, UnstyledButton } from '@mantine/core';
import * as Enums from '@/utils/enums';
import { DEMO_CARD_TEXT, ONBOARDING_PARTNERS } from '@/PageComponents/Onboarding/onboarding-config';
import { PrivacyNote } from '@/PageComponents/Onboarding/components/IntegrationSync';
import BookDemoCard, { useIsOnboardingCompany } from '@/PageComponents/Onboarding/components/BookDemoCard';
import { ONBOARDING_EVENTS, packageSlug, trackOnboarding } from '@/PageComponents/Onboarding/onboarding-analytics';
import styles from '@/PageComponents/Onboarding/components/IntegrationConnectChooser.module.css';

export interface IntegrationConnectChooserProps {
    /** Partner names from GET /Integration/GetIntegrationPartners, e.g. "SageOne" (Enums.IntegrationPartner keys). */
    partners: string[];
    /** Starts connecting that partner (the page's own setup flow). */
    onConnect: (partner: string) => void;
}

/**
 * Settings > Integrations before anything is connected: pick a package to connect
 * (onboarding Figma, "Accounting integrations page"). Onboarding companies also get the
 * walkthrough offer (until a demo is booked) and a way back to their setup steps.
 */
const IntegrationConnectChooser: FC<IntegrationConnectChooserProps> = ({ partners, onConnect }) => {
    // The packages this company can connect, in the order the design shows them.
    const available = ONBOARDING_PARTNERS
        .map(p => ({ ...p, key: partners.find(name => (Enums.IntegrationPartner as Record<string, number>)[name] === p.partner) }))
        .filter((p): p is typeof p & { key: string } => !!p.key);

    const onboarding = useIsOnboardingCompany();

    if (available.length === 0) return <Text>Select an integration partner to continue</Text>;

    const connect = (p: typeof available[number]) => {
        if (onboarding) trackOnboarding(ONBOARDING_EVENTS.connectClicked, { package: packageSlug(p.name), location: 'integrations_settings' });
        onConnect(p.key);
    };

    return (
        <Box className={styles.section} data-testid="integration-connect-chooser">
            <Text className={styles.heading}>Choose your existing accounting system</Text>
            <div className={styles.options}>
                {available.map(p => (
                    <UnstyledButton key={p.partner} id={`integration-connect-${packageSlug(p.name).replace(/_/g, '-')}`} className={styles.option} onClick={() => connect(p)}>
                        <Text className={styles.optionName}>Connect to {p.name}</Text>
                        <Image src={p.logoUrl} alt="" w={44} h={44} fit="contain" className={styles.optionLogo} />
                    </UnstyledButton>
                ))}
            </div>
            <Box mt={16}><PrivacyNote /></Box>

            <BookDemoCard page="integrations" mt={28} description={DEMO_CARD_TEXT.integration} keepBackLink />
        </Box>
    );
};

export default IntegrationConnectChooser;
